import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useMemo,
} from "react";
import { useDispatch } from "react-redux";

import {
  flexRender,
  functionalUpdate,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { setCurrentTableStateAC, setTabsTableStateAC } from "store/actions/table";

import {
  DefaultHeaderWrapper,
  DefaultHeaderCell,
  DefaultHeaderRow,
  DefaultBodyWrapper,
  DefaultBodyCell,
  DefaultBodyRow,
  DefaultFooterWrapper,
  DefaultFooterRow,
  DefaultFooterCell,
} from "./TableComponents";
import MultipleChoice from "../CustomBodyCellV2/ui/MultipleChoice/MultipleChoice";
import { DefaultColumnSorter } from "./ColumnSorter";
import { GlobalFilter, globalFilterFn } from "./GlobalFilter";
import { getRowSelectionColumn } from "./RowSelection";

import { getTabs } from "store/selectors/tabsSettings";
import { colorElements } from "utils/builder/IScheduleLogic";
import { ColorConditionService } from "utils/services/colorCondition.service";
import { getBounding } from "utils/elementExt";

import { useSelector } from "react-redux";
import { useDashboardState } from "components/DashboardContext";
import classNames from "classnames";
const Table = (props) => {
  const {
    columns,
    data,
    footer,
    initialState,
    state,
    columnsInfo,
    onRow,
    withGlobalFilter,
    withColumnFilters,
    withSorting,
    withPagination,
    withColumnResizing,
    withRowSelection,
    withRowExpanding,
    components: {
      headerWrapper,
      headerRow,
      headerCell,
      headerCellCustomActions,
      bodyWrapper,
      bodyRow,
      bodyCell,
      footerWrapper,
      footerRow,
      footerCell,
      pagination,
      columnFilter,
      columnSorter,
      wrapperInner,
    } = {},
    classes = {},
    onPageChange,
    onSearch,
    onFilter,
    onSort,
    onRowSelectionChange,
    onExpandedChange,
    onRowsChange,
    tableRef,
    options,
    withOutActions,
    isExternalWindow,
    modelId = null,
  } = props;

  const [choicesState, setChoicesState] = useState(null);

  const { getColumnHeaderLabel } = useDashboardState()

  const DraggableTableHeader = ({ header, isSelectionCeil, isFirstRow }) => {

    const columnheaderLabel = getColumnHeaderLabel(header.column.columnDef)

    const { attributes, isDragging, listeners, setNodeRef, transform } =
      useSortable({
        id: header.column.id,
      });

    const style = {
      position: "relative",
      width: isSelectionCeil ? "120px" : header.getSize(),
      transform: CSS.Translate.toString(transform),
      overflow: 'hidden',
      transition: "width transform 0.2s ease-in-out",
      borderRadius: isDragging ? "5px 5px 0 0" : null,
      borderTop: isDragging ? "1px solid #FA8700" : "1px solid transparent",
      borderLeft: isDragging ? "1px solid #FA8700" : "1px solid transparent",
      borderRight: isDragging ? "1px solid #FA8700" : "1px solid transparent",
      background: isDragging ? "#FFF" : null,
      cursor: 'pointer',
    };

    return (
      <HeaderCell
        key={header.id}
        table={table}
        header={header}
        colSpan={header.colSpan}
        className={classes.th}
        role="columnheader"
        style={style}
      >
        {!isFirstRow && (
          <div
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              insetInlineStart: 0,
              top: 0,
              zIndex: 1,
            }}
            ref={setNodeRef}
            {...attributes}
            {...listeners}
          />
        )}

        <div className={classes.headerCellContent}>
          {flexRender(columnheaderLabel, header.getContext())}
        </div>

        <div
          className={classes.headerCellButtons}
          style={{
            position: "relative",
            zIndex: 10,
          }}
        >
          {header.column.getCanSort() ? (
            <ColumnSorter column={header.column} />
          ) : null}

          {HeaderCellCustomActions?.({ column: header.column })}
        </div>
      </HeaderCell>
    );
  };

  const DragAlongCell = ({ cell, row, isFirstRow, setChoicesState }) => {
    const { isDragging, setNodeRef, transform } = useSortable({
      id: cell.column.id,
    });

    const rowProps = onRow ? onRow(row) : {};

    const style = {
      position: "relative",
      transform: CSS.Translate.toString(transform),
      transition: "width transform 0.2s ease-in-out",
      width: isFirstRow ? "120px" : cell.column.getSize(),
      borderLeft: isDragging ? "1px solid #FA8700" : "1px solid transparent",
      borderRight: isDragging ? "1px solid #FA8700" : "1px solid transparent",
      background: isDragging ? "#FFF" : null,
      overflow: 'hidden',
    };

    return (
      <BodyCell
        key={cell.id}
        setNodeRef={setNodeRef}
        table={table}
        row={row}
        cell={cell}
        tableData={data}
        className={classes.td}
        choicesState={choicesState}
        setChoicesState={setChoicesState}
        style={style}
        role="cell"
        {...rowProps}
      >
        {flexRender(cell.column.columnDef.cell, cell.getContext())}
      </BodyCell>
    );
  };

  const HeaderWrapper = headerWrapper ?? DefaultHeaderWrapper;
  const HeaderRow = headerRow ?? DefaultHeaderRow;
  const HeaderCell = headerCell ?? DefaultHeaderCell;
  const BodyWrapper = bodyWrapper ?? DefaultBodyWrapper;
  const BodyRow = bodyRow ?? DefaultBodyRow;
  const BodyCell = bodyCell ?? DefaultBodyCell;
  const ColumnFilter = columnFilter;
  const ColumnSorter = columnSorter ?? DefaultColumnSorter;
  const HeaderCellCustomActions = headerCellCustomActions;
  const WrapperInner = wrapperInner;
  const FooterWrapper = footerWrapper ?? DefaultFooterWrapper;
  const FooterRow = footerRow ?? DefaultFooterRow;
  const FooterCell = footerCell ?? DefaultFooterCell;

  const table = useReactTable({
    data,
    columns: withRowSelection ? [getRowSelectionColumn(), ...columns] : columns,
    state,
    initialState,
    // Stable row ids to keep selection consistent across grouped/ungrouped views and persisted state
    getRowId: (row, index, parent) => {
      const ids =
        (Array.isArray(row?.lineElements) && row.lineElements) ||
        (Array.isArray(row?.original?.lineElements) && row.original.lineElements) ||
        [];
      if (ids.length) return `le:${ids.join("|")}`;
      const idx = Number.isFinite(index) ? String(index) : Math.random().toString(36).slice(2);
      return parent ? `${parent.id}.${idx}` : idx;
    },
    enableGlobalFilter: !!withGlobalFilter,
    globalFilterFn,
    enableColumnFilters: !!withColumnFilters,
    enableSorting: !!withSorting,
    enableColumnResizing: !!withColumnResizing,
    enableRowSelection: !!withRowSelection,
    enableExpanding: !!withRowExpanding,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel:
      options?.enableFilters || withGlobalFilter || withColumnFilters
        ? getFilteredRowModel()
        : undefined,
    getPaginationRowModel:
      options?.enableFilters || withPagination
        ? getPaginationRowModel()
        : undefined,
    getSortedRowModel:
      options?.enableSorting || withSorting ? getSortedRowModel() : undefined,
    getExpandedRowModel:
      options?.enableExpanding || withRowExpanding
        ? getExpandedRowModel()
        : undefined,
    // getRowCanExpand,

    ...options,
  });

  const tableContainerRef = useRef(null);
  const dispatch = useDispatch();

  const handleChange = useCallback(
    (key, handler) => (arg0) => {
      if (state && state[key]) {
        const next = functionalUpdate(arg0, state[key]);
        
        if (key === 'rowSelection') {
          try {
            const { getCurrentNamespace } = require('utils/crossTabSync');
            const currentNs = getCurrentNamespace() || modelId;
            const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
            localStorage.setItem(rowSelectionKey, JSON.stringify(next));
          } catch (err) {
            console.error('[TableBase] Error writing rowSelection', err);
          }
        }
        
        handler && handler(next);

        return;
      }

      table.setState((state) => {
        const next = functionalUpdate(arg0, state[key]);
        
        if (key === 'rowSelection') {
          try {
            const { getCurrentNamespace } = require('utils/crossTabSync');
            const currentNs = getCurrentNamespace() || modelId;
            const rowSelectionKey = currentNs ? `rowSelection:${currentNs}` : (modelId ? `rowSelection:${modelId}` : 'rowSelection');
            localStorage.setItem(rowSelectionKey, JSON.stringify(next));
          } catch (err) {
            console.error('[TableBase] Error writing rowSelection', err);
          }
        }
        
        handler && handler(next);

        return {
          ...state,
          [key]: next,
        };
      });
    },
    [state, table, modelId, isExternalWindow]
  );
  
  useEffect(() => {
    table.setOptions((prev) => ({
      ...prev,
      onGlobalFilterChange: handleChange("globalFilter", onSearch),
      onColumnFiltersChange: handleChange("columnFilters", onFilter),
      onSortingChange: handleChange("sorting", onSort),
      onPaginationChange: handleChange("pagination", onPageChange),
      onRowSelectionChange: handleChange("rowSelection", onRowSelectionChange),
      onExpandedChange: handleChange("expanded", onExpandedChange),
    }));
  }, [
    handleChange,
    onExpandedChange,
    onFilter,
    onPageChange,
    onRowSelectionChange,
    onSearch,
    onSort,
    table,
  ]);

  function handleDragEnd(event) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      function findIndexById(array, id, isColumnsInfo) {
        for (let i = 0; i < array.length; i++) {
          if (isColumnsInfo) {
            if (array[i].headerKey === id) {
              return i;
            }
          } else {
            if (array[i].id === id) {
              return i;
            }
          }
        }
        return -1;
      }

      const oldIndex = findIndexById(columns, active.id, false);
      const newIndex = findIndexById(columns, over.id, false);
      const setColumns = arrayMove(columns, oldIndex, newIndex);
      const columnsInfoOldIndex = findIndexById(columnsInfo, active.id, true);
      const columnsInfoNewIndex = findIndexById(columnsInfo, over.id, true);

      if (columnsInfoOldIndex >= 0 && columnsInfoNewIndex >= 0) {
        const setColumnsInfo = arrayMove(columnsInfo, columnsInfoOldIndex, columnsInfoNewIndex);

        dispatch(
          setTabsTableStateAC({
            columnsInfo: setColumnsInfo
          })
        );
      };

      dispatch(
        setCurrentTableStateAC({
          columns: setColumns,
        })
      );

      table.getAllColumns();
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  useImperativeHandle(tableRef, () => table);

  const { rows } = table.getRowModel();
  const tabSetting = useSelector(getTabs)?.[1] ?? null;

  useEffect(() => {
    setTimeout(() => {
      if (!!rows?.length) {
        rows?.map(item => {
          item?.getAllCells().map(cell => {
            const { colorScheme, uniqueColorsData } = tabSetting?.[cell?.column?.id] ?? { colorScheme: null, uniqueColorsData: null };

            const colorDataCellItem = ColorConditionService.getColorByColorScheme({
              colorScheme: colorScheme,
              uniqueColorsData: uniqueColorsData,
              tableData: data,
              currentValue: cell?.getValue(),
            });

            if (colorScheme?.isPrimaryColumn && colorDataCellItem?.color) {
              colorElements(item?.original?.lineElements, colorDataCellItem?.color);
            };
          });
        });
      };
    }, 1000)
  }, [tabSetting, rows])

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 45,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
        navigator.userAgent.indexOf('Firefox') === -1
        ? element => getBounding(element)?.height
        : undefined,
    overscan: 5,
  });
  const virtualRows = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    onRowsChange?.(rows);
  }, [onRowsChange, rows]);

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <MultipleChoice
        choicesState={choicesState}
        setChoicesState={setChoicesState}
        data={data}
      />

      <div
        ref={tableContainerRef}
        className={classNames({
          [classes.wrapper]: true,
          [classes.wrapperNoPadding]: !!withOutActions,
          [classes.wrapperTableWindow]: !!isExternalWindow,
        })}
      >
        {WrapperInner?.({
          table,
          globalFilter: withGlobalFilter ? GlobalFilter : null,
        })}

        <table className={isExternalWindow ? classes.tableNew : classes.table}>
          <HeaderWrapper table={table} className={classes.thead}>
            {table.getHeaderGroups().map((headerGroup) => (
              <HeaderRow
                key={headerGroup.id}
                table={table}
                headerGroup={headerGroup}
                role="row"
                className={classes.tr}
              >
                <SortableContext
                  items={columns}
                  strategy={horizontalListSortingStrategy}
                >
                  {headerGroup.headers.map((header) => {
                    const isFirstRow = header.index === 0;
                    const isSelectionCeil = withRowSelection && isFirstRow;

                    return (
                      <DraggableTableHeader
                        key={header.id}
                        header={header}
                        isSelectionCeil={isSelectionCeil}
                        isFirstRow={isFirstRow}
                      />
                    );
                  })}
                </SortableContext>
              </HeaderRow>
            ))}
          </HeaderWrapper>

          <BodyWrapper
            table={table}
            className={classes.tbody}
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {virtualRows.map((virtualRow, i) => {
              const row = rows[virtualRow.index];

              return (
                <BodyRow
                  dataIndex={virtualRow.index}
                  bodyRef={node => rowVirtualizer.measureElement(node)}
                  table={table}
                  row={row}
                  key={row.id}
                  className={classes.tr}
                  style={{
                    transform: `translateY(${virtualRow.start}px)`
                  }}
                  role="row"
                >
                  {row.getVisibleCells().map((cell, index) => {
                    const isFirstRow = index === 0;
                    return (
                      <SortableContext
                        key={cell.id}
                        items={columns}
                        strategy={horizontalListSortingStrategy}
                      >
                        <DragAlongCell
                          key={cell.id}
                          cell={cell}
                          row={row}
                          isFirstRow={isFirstRow}
                          setChoicesState={setChoicesState}
                          choicesState={choicesState}
                        />
                      </SortableContext>
                    );
                  })}
                </BodyRow>
              );
            })}
          </BodyWrapper>

          <FooterWrapper className={classes.tfoot}>
            {table.getHeaderGroups().map((footerGroup) => (
              <FooterRow
                key={footerGroup.id}
                table={table}
                role="row"
                className={classes.tr}
              >
                {footerGroup.headers.map((header) => {
                  return (
                    <FooterCell
                      key={header.id}
                      table={table}
                      header={header}
                      footer={footer}
                      colSpan={header.colSpan}
                      style={{
                        width: header.column.getSize(),
                        overflow: 'hidden',
                      }}
                      className={classes.td}
                      role="columnfooter"
                    >
                      {flexRender(footer[header.id], header.getContext())}
                    </FooterCell>
                  );
                })}
              </FooterRow>
            ))}
          </FooterWrapper>
        </table>
      </div>
    </DndContext>
  );
};

export default Table;
